import { Navigate, Route, Routes, useLocation } from "react-router";
import { Suspense, lazy, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Layout } from "./components/Layout.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { ScrollToTop } from "./components/ScrollToTop.js";
import { trackPageview, loadAnalytics } from "./lib/analytics.js";
import { Home } from "./pages/Home.js";
import { Watch } from "./pages/Watch.js";
import { Read } from "./pages/Read.js";
import { ContentDetail } from "./pages/ContentDetail.js";
import { DirtTalkArchive } from "./pages/DirtTalkArchive.js";
import { Posts } from "./pages/Posts.js";
import { DirtTalkDay } from "./pages/DirtTalkDay.js";
import { RigOfWeek } from "./pages/RigOfWeek.js";
import { RigOfWeekArchive } from "./pages/RigOfWeekArchive.js";
import { EquipmentDetail } from "./pages/EquipmentDetail.js";
import { OperatorDetail } from "./pages/OperatorDetail.js";
import { CompanyProfile } from "./pages/CompanyProfile.js";
import { BuiltNearYou } from "./pages/BuiltNearYou.js";
import { Quote } from "./pages/Quote.js";
import { About } from "./pages/About.js";
import { Privacy } from "./pages/Privacy.js";
import { Terms } from "./pages/Terms.js";
import { NotFound } from "./pages/NotFound.js";
import { SignIn } from "./pages/SignIn.js";
import { Verify } from "./pages/Verify.js";

// Lazy-load the surfaces no anonymous visitor ever sees on first paint:
// admin dashboard (heaviest, ~half the bundle), company management, the
// signed-in Loader Bucket. Public pages stay eager so they hit FCP fast.
const LoaderBucket = lazy(() =>
  import("./pages/LoaderBucket.js").then((m) => ({ default: m.LoaderBucket })),
);
const CompanyDashboard = lazy(() =>
  import("./pages/CompanyDashboard.js").then((m) => ({ default: m.CompanyDashboard })),
);
const Dashboard = lazy(() =>
  import("./pages/Dashboard.js").then((m) => ({ default: m.Dashboard })),
);

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function RouteFallback() {
  return (
    <Box sx={{ p: 8, textAlign: "center" }}>
      <CircularProgress />
    </Box>
  );
}

export function App() {
  // No cookie-consent gate — Australia doesn't require GDPR-style opt-in. GA4
  // loads on startup (anonymised IP); the privacy policy discloses it.
  useEffect(() => {
    loadAnalytics();
  }, []);

  return (
    <>
      <ScrollToTop />
      <PageviewTracker />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/dashboard/*" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
          <Route element={<Layout />}>
            <Route index element={<Home />} />

            {/* Primary content surfaces */}
            <Route path="/watch" element={<Watch />} />
            <Route path="/read" element={<Read />} />
            <Route path="/about" element={<About />} />

            {/* Secondary surfaces (kept reachable but not in nav) */}
            <Route path="/content/:slug" element={<ErrorBoundary fallbackTitle="Couldn't load this article"><ContentDetail /></ErrorBoundary>} />
            <Route path="/dirt-talk" element={<DirtTalkArchive />} />
            <Route path="/dirt-talk/:date" element={<DirtTalkDay />} />
            <Route path="/posts" element={<Posts />} />
            <Route path="/rig-of-the-week" element={<RigOfWeek />} />
            <Route path="/rig-of-week/archive" element={<RigOfWeekArchive />} />
            <Route path="/equipment/:slug" element={<EquipmentDetail />} />
            <Route path="/operators/:slug" element={<OperatorDetail />} />
            <Route path="/company/:slug" element={<ErrorBoundary fallbackTitle="Couldn't load this company"><CompanyProfile /></ErrorBoundary>} />
            <Route path="/built-near-you/:postcode" element={<BuiltNearYou />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Auth + user area (lazy) */}
            <Route path="/auth/sign-in" element={<SignIn />} />
            <Route path="/auth/verify" element={<Verify />} />
            <Route path="/loader-bucket" element={<LoaderBucket />} />
            <Route path="/me/company" element={<CompanyDashboard />} />

            {/* Redirects from old structure */}
            <Route path="/content" element={<Navigate to="/watch" replace />} />
            <Route path="/contact" element={<Navigate to="/about#contact" replace />} />
            <Route path="/for-corporates" element={<Navigate to="/about#companies" replace />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
